import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type SelectOption = { value: string; label: string };

type Props = {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  // Extra width/size classes for the wrapper (e.g. 'sm:w-44').
  className?: string;
};

// A small themed dropdown (shared, like ClearSearchButton/ActionColumn). A native <select>'s
// option popup is drawn by the OS and can't be themed, so this renders its own list: options
// paint primary (purple in the dark theme) on hover/keyboard-focus — matching the user search
// on the Users page (UserAutocomplete) — with type-ahead and arrow-key navigation. Open/closed
// is explicit state (not daisyUI focus-within, which can't represent "focused but closed") so
// Escape closes just the menu, clicking the trigger toggles it, and picking an option refocuses
// the trigger.
export default function HighlightSelect({ label, value, options, onChange, className }: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = options.find((option) => option.value === value);

  const focusOption = (index: number) =>
    (listRef.current?.children[index] as HTMLElement | undefined)?.focus();

  // Focusing an option needs the list mounted; if the menu is closed, open it and defer the
  // focus to the layout effect below, which runs once the <ul> has rendered.
  const pendingFocus = useRef<number | null>(null);
  const focusOptionSoon = (index: number) => {
    if (open) {
      focusOption(index);
    } else {
      pendingFocus.current = index;
      setOpen(true);
    }
  };

  const closeMenu = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (option: SelectOption) => {
    onChange(option.value);
    closeMenu();
  };

  // Type-ahead, matching a native <select>: typing accumulates a buffer and focuses the first
  // option whose label starts with it ('n' → first N option, 'net' → Netherlands). The buffer
  // resets after a short pause and whenever the menu closes (below). Space is left to select.
  const typeaheadBuffer = useRef('');
  const typeaheadTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(typeaheadTimer.current), []);

  // On open: consume any deferred focus target. On close: reset the type-ahead buffer so a
  // reopen within the timeout window starts fresh.
  useLayoutEffect(() => {
    if (open) {
      if (pendingFocus.current != null) {
        focusOption(pendingFocus.current);
        pendingFocus.current = null;
      }
    } else {
      typeaheadBuffer.current = '';
      clearTimeout(typeaheadTimer.current);
    }
  }, [open]);

  const handleTypeahead = (e: React.KeyboardEvent) => {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    // Space is a search character here — so multi-word labels ("New Zealand", "Human
    // Resources") are reachable by type-ahead — and never a selection key (only Enter
    // selects). preventDefault always, so a focused option doesn't scroll the list on space;
    // ignore a leading space so it can't poison the match buffer.
    e.preventDefault();
    if (e.key === ' ' && typeaheadBuffer.current === '') return;
    clearTimeout(typeaheadTimer.current);
    typeaheadTimer.current = setTimeout(() => (typeaheadBuffer.current = ''), 700);
    typeaheadBuffer.current += e.key.toLowerCase();
    const match = options.findIndex((option) =>
      option.label.toLowerCase().startsWith(typeaheadBuffer.current)
    );
    if (match >= 0) focusOptionSoon(match);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Close only the menu and keep focus on the trigger; let a closed-menu Escape bubble to
      // the modal's handler (so Escape there closes the modal).
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOptionSoon(0);
    } else if (e.key === 'Enter' || e.key === ' ') {
      // preventDefault stops the browser also firing a click (which would re-toggle).
      e.preventDefault();
      setOpen((wasOpen) => !wasOpen);
    } else {
      handleTypeahead(e);
    }
  };

  const onOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, option: SelectOption) => {
    // Only Enter selects — space is reserved for type-ahead (see handleTypeahead) so labels
    // with spaces can be searched without the space picking the focused option.
    if (e.key === 'Enter') {
      e.preventDefault();
      choose(option);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      (e.currentTarget.nextElementSibling as HTMLElement | null)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const previous = e.currentTarget.previousElementSibling as HTMLElement | null;
      if (previous) previous.focus();
      else triggerRef.current?.focus();
    } else {
      handleTypeahead(e);
    }
  };

  // Close when focus leaves the whole control (tab away / click outside).
  const onBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  };

  return (
    // A <div>, not a <label>: a <button> is a labelable element, so a wrapping <label> would
    // hijack the trigger's accessible name. The trigger carries its own aria-label instead.
    <div
      ref={containerRef}
      className={`flex flex-col gap-1 w-full ${className ?? ''}`}
      onBlur={onBlur}
    >
      <span className="text-xs font-medium opacity-70">{label}</span>
      <div className="relative w-full">
        <button
          ref={triggerRef}
          type="button"
          className="select select-bordered select-sm w-full text-left font-normal"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${label}: ${selected?.label ?? ''}`}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          onKeyDown={onTriggerKeyDown}
        >
          {selected?.label}
        </button>
        {open && (
          <ul
            ref={listRef}
            role="listbox"
            className="menu menu-sm absolute left-0 right-0 top-full z-20 mt-1 max-h-64 flex-nowrap overflow-y-auto rounded-md border border-base-200 bg-base-100 shadow"
          >
            {options.map((option) => (
              <li
                key={option.value}
                tabIndex={0}
                role="option"
                aria-selected={option.value === value}
                onClick={() => choose(option)}
                onKeyDown={(e) => onOptionKeyDown(e, option)}
                className="group focus:outline-none"
              >
                {/* Hover/keyboard-focus paints the option primary (purple in the dark theme).
                    The `!` (important) is load-bearing: daisyUI 5's `.menu` hover/focus rules
                    live in a cascade layer declared after Tailwind's `utilities`, so a plain
                    `group-hover:bg-primary` loses to daisyUI's default grey on hover (its focus
                    rule only uses `:focus-visible`, which is why arrow-key focus already won).
                    important beats the unlayered daisyUI rules either way. */}
                <span className="block w-full truncate rounded-md group-hover:bg-primary! group-hover:text-primary-content! group-focus:bg-primary! group-focus:text-primary-content!">
                  {option.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
